import { useEffect, useRef, type ReactNode } from 'react';
import { LDProvider, useFlags, useLDClient } from 'launchdarkly-react-client-sdk';
import type { FeatureFlag } from '@metycle/shared';
import { LAUNCHDARKLY_CLIENT_ID } from './feature-flags-client-id';
import { FORCED_ON_FLAGS } from './feature-flags-force-on';

// The only module importing the LaunchDarkly SDK. Components consume flags
// through useFeatureFlag; main.tsx mounts FeatureFlagsProvider; AppShell
// mounts FeatureFlagIdentity.

export function FeatureFlagsProvider({
  children,
  clientSideID = LAUNCHDARKLY_CLIENT_ID,
}: Readonly<{ children: ReactNode; clientSideID?: string | undefined }>) {
  if (clientSideID === undefined || clientSideID === '') {
    return <>{children}</>;
  }
  return (
    <LDProvider
      clientSideID={clientSideID}
      // streaming off (legacy-proven cost posture): flags are fetched once
      // per page load; a dashboard change shows on the next reload.
      options={{ streaming: false }}
      reactOptions={{ useCamelCaseFlagKeys: false }}
    >
      {children}
    </LDProvider>
  );
}

export function useFeatureFlag(flag: FeatureFlag, defaultValue = false): boolean {
  const flags = useFlags();
  if (FORCED_ON_FLAGS.has(flag)) {
    return true;
  }
  const value: unknown = flags[flag];
  return typeof value === 'boolean' ? value : defaultValue;
}

const ANONYMOUS_CONTEXT = { kind: 'user', key: 'anonymous', anonymous: true } as const;

// Re-identifies the LD client as the signed-in user so per-user targeting
// works; reverts to anonymous when the key drops (sign-out, impersonation
// end) or the shell unmounts. identify() is best-effort — a failed call
// just leaves the previous flag set in place.
export function FeatureFlagIdentity({ userKey }: Readonly<{ userKey: string | null }>) {
  const ldClient = useLDClient();
  const identifiedKey = useRef<string | null>(null);

  useEffect(() => {
    if (ldClient === undefined || userKey === identifiedKey.current) {
      return;
    }
    identifiedKey.current = userKey;
    ldClient
      .identify(userKey === null ? ANONYMOUS_CONTEXT : { kind: 'user', key: userKey })
      .catch(() => undefined);
  }, [ldClient, userKey]);

  useEffect(() => {
    if (ldClient === undefined) {
      return;
    }
    return () => {
      if (identifiedKey.current !== null) {
        identifiedKey.current = null;
        ldClient.identify(ANONYMOUS_CONTEXT).catch(() => undefined);
      }
    };
  }, [ldClient]);

  return null;
}
